import { OrdinalNumbers } from '@/constants/ordinal-numbers.const';

export function formatCurrency(currencyValue?: number | null, decimalPlaces: number = 0): string {
  if (currencyValue == undefined) return '';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'AUD',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: decimalPlaces, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: decimalPlaces, // (causes 2500.99 to be printed as $2,501)
  });
  return formatter.format(currencyValue).replace('A', '').trim();
}

export function toProperCase(str = '') {
  if (!str || str == '' || typeof str !== 'string') return;
  return str.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
}

export function addOrdinalSuffix(
  stepNumber: number | undefined,
  text: string | undefined = '',
  textWithPlaceholder: string | null = null
): string {
  // Return an empty string if both text and textWithPlaceholder are missing
  if (!text && !textWithPlaceholder) return '';

  // If stepNumber is null, return text or textWithPlaceholder directly
  if (stepNumber == null) return text ?? textWithPlaceholder ?? '';

  // Lookup ordinal suffix or default to empty string if stepNumber is out of range
  const ordinalText = OrdinalNumbers[stepNumber] ?? '';

  // Use textWithPlaceholder if provided, replacing placeholder with ordinalText
  if (textWithPlaceholder) {
    return textWithPlaceholder.replace('{ordinalSuffix}', ordinalText).trim();
  }

  // Fallback to appending ordinalText to text
  return `${ordinalText} ${text}`.trim();
}

/**
 * Format masked card number
 *
 * @param cardNumber 1234XXXXXXXX5678
 * @returns **** **** **** 1234
 */
export function formatMaskedCardNumber(cardNumber: string) {
  try {
    return '**** **** **** ' + cardNumber.slice(cardNumber.length - 4, cardNumber.length);
  } catch {
    return cardNumber;
  }
}

export function lowerCaseSubsequentWords(str: string) {
  return str
    .split(' ')
    .map((word, index) => (index === 0 ? word : word.charAt(0).toLowerCase() + word.slice(1)))
    .join(' ');
}

export function numberToWord(num: number): string {
  // Dictionary for basic number words
  const numberWords: { [key: string]: string } = {
    '0': 'Zero',
    '1': 'One',
    '2': 'Two',
    '3': 'Three',
    '4': 'Four',
    '5': 'Five',
    '6': 'Six',
    '7': 'Seven',
    '8': 'Eight',
    '9': 'Nine',
    '10': 'Ten',
    '11': 'Eleven',
    '12': 'Twelve',
    '13': 'Thirteen',
    '14': 'Fourteen',
    '15': 'Fifteen',
    '16': 'Sixteen',
    '17': 'Seventeen',
    '18': 'Eighteen',
    '19': 'Nineteen',
    '20': 'Twenty',
    '30': 'Thirty',
    '40': 'Forty',
    '50': 'Fifty',
    '60': 'Sixty',
    '70': 'Seventy',
    '80': 'Eighty',
    '90': 'Ninety',
  };

  // Validate input
  if (isNaN(num) || num < 0 || num > 999 || !Number.isInteger(num)) {
    return num.toString(); // Return input as string if invalid or out of range
  }

  // Handle zero and single-digit numbers
  if (num <= 19 || numberWords[num.toString()]) {
    return numberWords[num.toString()];
  }

  // Handle numbers 20-99
  if (num <= 99) {
    const tens = Math.floor(num / 10) * 10;
    const ones = num % 10;
    return ones === 0
      ? numberWords[tens.toString()]
      : `${numberWords[tens.toString()]} ${numberWords[ones.toString()]}`;
  }

  // Handle numbers 100-999
  const hundreds = Math.floor(num / 100);
  const remainder = num % 100;
  const result = `${numberWords[hundreds.toString()]} Hundred`;

  if (remainder === 0) {
    return result;
  } else if (remainder <= 19 || numberWords[remainder.toString()]) {
    return `${result} ${numberWords[remainder.toString()]}`;
  } else {
    const tens = Math.floor(remainder / 10) * 10;
    const ones = remainder % 10;
    return ones === 0
      ? `${result} ${numberWords[tens.toString()]}`
      : `${result} ${numberWords[tens.toString()]} ${numberWords[ones.toString()]}`;
  }
}
